import React, { useEffect, useRef, useState } from "react";
import Barcode from "react-barcode";
import {
  CheckIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

type ScannableBarcodeProps = {
  value?: string | number | null;
  className?: string;
};

const GROUP_SEPARATOR = "\u001d";

function barcodeValue(value: string | number | null | undefined) {
  return value == null ? "" : String(value).split(GROUP_SEPARATOR).join("").trim();
}

export default function ScannableBarcode({
  value,
  className = "",
}: ScannableBarcodeProps) {
  const barcode = barcodeValue(value);
  if (!barcode) {
    return <span className="text-sm text-gray-900">N/A</span>;
  }

  return (
    <div className={className}>
      <BarcodeValue value={barcode} />
      <div
        className="mt-1 overflow-x-auto rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-1"
        aria-label={`Developer barcode preview for ${barcode}`}
      >
        <Barcode
          value={barcode}
          format="CODE128"
          displayValue={false}
          height={36}
          width={1}
          fontSize={10}
          margin={0}
          background="transparent"
        />
      </div>
    </div>
  );
}

function BarcodeValue({ value }: { value: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    },
    [],
  );

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(value);
      setStatus("copied");
    } catch {
      setStatus("error");
    }

    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
    }
    resetTimer.current = setTimeout(() => setStatus("idle"), 2000);
  };

  return (
    <span className="relative inline-block min-w-0 pr-5">
      <span className="block break-all text-sm text-gray-900">{value}</span>
      <span className="absolute -right-1 -top-1.5 inline-flex">
        <button
          type="button"
          aria-label="Copy full barcode"
          title="Copy full barcode"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-gray-700"
          onClick={copy}
        >
          {status === "copied" ? (
            <CheckIcon className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
          ) : status === "error" ? (
            <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-600" aria-hidden="true" />
          ) : (
            <DocumentDuplicateIcon className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
        {status !== "idle" ? (
          <span
            role="status"
            className={`pointer-events-none absolute bottom-full right-0 z-50 mb-1 whitespace-nowrap rounded px-2 py-1 text-xs text-white shadow-sm ${
              status === "error" ? "bg-red-600" : "bg-gray-900"
            }`}
          >
            {status === "copied" ? "Barcode copied" : "Could not copy barcode"}
          </span>
        ) : null}
      </span>
    </span>
  );
}
